/**
 * The animation runtime, loaded on demand and exactly once.
 *
 * GSAP is imported dynamically rather than at the top of a module, and that is the whole
 * point of this file. A static import would put GSAP and ScrollTrigger in the entry chunk
 * of every build — including the Tizen widget, which never animates anything (see
 * `decideMotion`) and would carry the bytes to the panel for nothing. Here, a device that
 * decides against motion never issues the request at all.
 *
 * The promise is memoised, so twenty rails asking for the runtime in the same frame
 * produce one network request and one `registerPlugin` call.
 */
export interface MotionRuntime {
  gsap: typeof import('gsap').gsap;
  ScrollTrigger: typeof import('gsap/ScrollTrigger').ScrollTrigger;
}

let pending: Promise<MotionRuntime> | null = null;

export function loadMotionRuntime(): Promise<MotionRuntime> {
  pending ??= (async () => {
    const [core, plugin] = await Promise.all([import('gsap'), import('gsap/ScrollTrigger')]);
    const { gsap } = core;
    const { ScrollTrigger } = plugin;
    gsap.registerPlugin(ScrollTrigger);
    return { gsap, ScrollTrigger };
  })();
  return pending;
}

/** Whether the runtime is already in memory — lets a caller skip a frame of waiting. */
export function motionRuntimeLoaded(): boolean {
  return pending !== null;
}
